<script setup lang="ts">
import {faCartShoppingFast} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {useCheckoutStore} from "@/checkout/stores/checkout.store.ts";
import {useI18n} from "vue-i18n";
import ResponsiveImage from "@/shared/components/image/ResponsiveImage.vue";
import MobileAndTabletView from "@/shared/components/ui/viewport/MobileAndTabletView.vue";
import {DesktopView} from "@/shared/components/ui/viewport";
import {AppImage} from "@/shared/graphql/types";
import {computed} from "vue";
import {useCheckoutFunctions} from "@/checkout/lib/common.ts";

interface Props {
  step: number
}

defineProps<Props>()

const checkout = useCheckoutStore();
const {t} = useI18n()
const {cancelCheckout} = useCheckoutFunctions()

const logo = computed(() => {
  return checkout.app?.images?.find((image: AppImage) => image.name == "logo")?.image?.responsive
});
</script>
<template>

  <header class="relative border-b border-gray-200 bg-white text-sm font-medium text-gray-700">
    <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div class="relative flex justify-between items-center">
        <ResponsiveImage :image="logo" sizes="180px" class="max-h-14"/>
        <MobileAndTabletView>
          <nav class="flex items-center justify-center" aria-label="Progress">
            <p class="text-sm font-medium">{{ t('header.checkout') }}</p>
            <ol role="list" class="ml-8 flex items-center space-x-5">
              <li>
                <!-- Completed Step -->
                <a href="#" class="block size-2.5 rounded-full bg-primary">
                  <span class="sr-only">{{ t('header.basket') }}</span>
                </a>
              </li>
              <li>
                <!-- Current Step -->
                <a href="#" class="relative flex items-center justify-center" aria-current="step">
                  <span class="absolute flex size-5 p-px" aria-hidden="true">
                    <span class="size-full rounded-full bg-primary/40"></span>
                  </span>
                  <span class="relative block size-2.5 rounded-full bg-primary" aria-hidden="true"></span>
                  <span class="sr-only">{{ t('header.checkout') }}</span>
                </a>
              </li>
              <li>
                <!-- Upcoming Step -->
                <a href="#" class="block size-2.5 rounded-full bg-gray-200 hover:bg-gray-400">
                  <span class="sr-only">{{ t('header.confirmation') }}</span>
                </a>
              </li>
            </ol>
          </nav>
        </MobileAndTabletView>
        <DesktopView>
          <nav aria-label="Progress" class="w-3/5">
            <ol role="list" class="space-y-4 md:flex md:space-x-8 md:space-y-0">
              <li class="md:flex-1">
                <!-- Completed Step -->
                <a href="#" @click.prevent="cancelCheckout"
                   class="group flex flex-col border-l-4 border-primary py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
                  <span class="text-sm font-medium text-primary group-hover:text-primary/70">Stap 1</span>
                  <span class="text-sm font-medium">{{ t('header.basket') }}</span>
                </a>
              </li>
              <li class="md:flex-1">
                <!-- Current Step -->
                <a href="#" class="flex flex-col border-l-4 py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4"
                   :class="step >= 2 ? 'border-primary' : 'border-gray-200'">
                  <span class="text-sm font-medium"
                        :class="step >= 2 ? 'text-primary group-hover:text-primary/70' : 'text-slate-500 group-hover:text-gray-700'">Stap 2</span>
                  <span class="text-sm font-medium">{{ t('header.checkout') }}</span>
                </a>
              </li>
              <li class="md:flex-1">
                <!-- Upcoming Step -->
                <a href="#" class="group flex flex-col border-l-4 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4"
                   :class="step == 3 ? 'border-primary' : 'border-gray-200'">
                  <span class="text-sm font-medium"
                        :class="step == 3 ? 'text-primary group-hover:text-primary/70' : 'text-slate-500 group-hover:text-gray-700'">Stap 3</span>
                  <span class="text-sm font-medium">{{ t('header.confirmation') }}</span>
                </a>
              </li>
            </ol>
          </nav>
          <div class="text-gray-400">
            <slot>
              <a v-if="step < 3" href="#" @click="cancelCheckout">
                <FontAwesomeIcon :icon="faCartShoppingFast" class="mr-2 hover:text-primary" size="xl"/>
              </a>
              <a v-if="step == 3" href="#" @click="cancelCheckout">
                <FontAwesomeIcon :icon="faCartShoppingFast" class="mr-2 text-primary hover:text-primary/70" size="xl"/>
              </a>
            </slot>
          </div>
        </DesktopView>
      </div>
    </div>
  </header>
</template>
